import { useMemo, useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Workflow,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Play,
  ChevronDown,
  ChevronRight,
  Terminal,
} from 'lucide-react';
import {
  ReactFlow,
  Background,
  Controls,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useWorkflow, useWorkflowRuns, useExecuteWorkflow } from '../hooks/useWorkflows';
import { WorkflowStepNode, STEP_COLORS, type StepNodeData, type FieldDef } from '../components/WorkflowStepNode';
import { WorkflowInputForm } from '../components/WorkflowInputForm';
import { WORKFLOW_DESCRIPTIONS, STEP_DESCRIPTIONS, STEP_TOOLS } from '../data/workflow-metadata';
import type { WorkflowDefinition, JsonSchema, WorkflowRun } from '../types/workflow';

const nodeTypes = { stepNode: WorkflowStepNode };

const NODE_WIDTH = 440;
const NODE_GAP = 100;

// ---------------------------------------------------------------------------
// Schema helpers
// ---------------------------------------------------------------------------

function parseSchema(schema?: string | JsonSchema): JsonSchema | undefined {
  if (!schema) return undefined;
  if (typeof schema === 'string') {
    try {
      const parsed = JSON.parse(schema);
      return parsed.json ?? parsed;
    } catch {
      return undefined;
    }
  }
  return schema;
}

function getFields(schema?: string | JsonSchema): FieldDef[] {
  const parsed = parseSchema(schema);
  if (!parsed) return [];

  // Array schema — show the item properties if available
  if (parsed.type === 'array') {
    const items = (parsed as any).items;
    if (items?.properties) {
      const req = new Set<string>(items.required ?? []);
      return Object.entries(items.properties as Record<string, { type?: string }>).map(
        ([name, def]) => ({ name, type: def.type, optional: !req.has(name) })
      );
    }
    return [{ name: 'items', type: 'array' }];
  }

  if (!parsed.properties) return [];
  const required = new Set<string>(parsed.required ?? []);
  return Object.entries(parsed.properties).map(([name, def]) => ({
    name,
    type: def.type,
    optional: !required.has(name),
  }));
}

function formatStepName(id: string): string {
  const spaced = id.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced
    .split(/[-_ ]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Graph builder
// ---------------------------------------------------------------------------

function buildGraph(workflow: WorkflowDefinition): { nodes: Node[]; edges: Edge[] } {
  const stepIds: string[] = workflow.stepGraph
    ? workflow.stepGraph
        .map((s) => (s as any).step?.id ?? (s as any).stepId ?? '')
        .filter(Boolean)
    : Object.keys(workflow.steps ?? {});

  const nodes: Node[] = stepIds.map((stepId, index) => {
    const step = workflow.steps?.[stepId];
    const color = STEP_COLORS[index % STEP_COLORS.length];
    const data: StepNodeData = {
      stepIndex: index + 1,
      label: formatStepName(stepId),
      description: STEP_DESCRIPTIONS[stepId],
      inputFields: getFields(step?.inputSchema),
      outputFields: getFields(step?.outputSchema),
      tools: STEP_TOOLS[stepId] ?? [],
      color,
    };
    return {
      id: stepId,
      type: 'stepNode',
      position: { x: index * (NODE_WIDTH + NODE_GAP), y: 0 },
      data,
    };
  });

  const edges: Edge[] = stepIds.slice(1).map((stepId, index) => ({
    id: `e-${stepIds[index]}-${stepId}`,
    source: stepIds[index],
    target: stepId,
    type: 'smoothstep',
    style: {
      stroke: STEP_COLORS[index % STEP_COLORS.length],
      strokeWidth: 2,
      opacity: 0.6,
    },
    animated: true,
  }));

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { icon: typeof CheckCircle2; color: string; label: string }> = {
  success: { icon: CheckCircle2, color: 'text-green-400', label: 'Success' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
  running: { icon: Loader2, color: 'text-blue-400', label: 'Running' },
  pending: { icon: Clock, color: 'text-yellow-400', label: 'Pending' },
  suspended: { icon: AlertTriangle, color: 'text-orange-400', label: 'Suspended' },
  canceled: { icon: XCircle, color: 'text-gray-400', label: 'Canceled' },
};

function RunStatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? { icon: Clock, color: 'text-muted-foreground', label: status };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm ${config.color}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'running' ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}

function formatTimestamp(ts: number): string {
  if (!ts) return '\u2014';
  const date = new Date(ts);
  return date.toLocaleString('en-CA', {
    timeZone: 'America/Montreal',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(startTs: number): string {
  const elapsed = Date.now() - startTs;
  if (elapsed < 1000) return `${elapsed}ms`;
  if (elapsed < 60000) return `${(elapsed / 1000).toFixed(1)}s`;
  return `${Math.floor(elapsed / 60000)}m ${Math.floor((elapsed % 60000) / 1000)}s`;
}

// ---------------------------------------------------------------------------
// Run detail panel (observability)
// ---------------------------------------------------------------------------

function RunDetailPanel({ run }: { run: WorkflowRun }) {
  const hasResult = run.result && Object.keys(run.result).length > 0;
  const hasError = !!run.error?.message;

  return (
    <div className="bg-slate-900/50 border-t border-slate-800 px-4 py-3 space-y-3">
      {/* Run metadata */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="font-mono">ID: {run.runId}</span>
        <span>Started: {formatTimestamp(run.timestamp)}</span>
        {run.timestamp && <span>Elapsed: {formatDuration(run.timestamp)}</span>}
      </div>

      {/* Error */}
      {hasError && (
        <div className="rounded border border-red-800/50 bg-red-950/30 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-red-400 mb-1">
            <XCircle className="h-3.5 w-3.5" />
            {run.error?.name ?? 'Error'}
          </div>
          <pre className="text-xs text-red-300/80 whitespace-pre-wrap font-mono overflow-x-auto">
            {run.error?.message}
          </pre>
        </div>
      )}

      {/* Result */}
      {hasResult && (
        <div className="rounded border border-slate-700/50 bg-slate-950/30 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-1">
            <Terminal className="h-3.5 w-3.5" />
            Result
          </div>
          <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono overflow-x-auto max-h-80 overflow-y-auto">
            {JSON.stringify(run.result, null, 2)}
          </pre>
        </div>
      )}

      {/* No data yet */}
      {!hasResult && !hasError && run.status === 'running' && (
        <div className="flex items-center gap-2 text-sm text-blue-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Workflow is executing...
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function WorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: workflow, isLoading, error } = useWorkflow(id!);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [fastPoll, setFastPoll] = useState(false);
  const [showInputForm, setShowInputForm] = useState(false);

  const { data: runsData } = useWorkflowRuns(id!, fastPoll);
  const displayRuns: WorkflowRun[] = runsData?.runs ?? [];
  const totalRuns = runsData?.total ?? 0;
  const activeRunExists = displayRuns.some((r) => r.status === 'running' || r.status === 'pending');

  // Auto-enable fast polling when active runs detected, disable when idle
  useEffect(() => {
    setFastPoll(activeRunExists);
  }, [activeRunExists]);

  const executeMutation = useExecuteWorkflow(id!);

  const { nodes, edges } = useMemo(() => {
    if (!workflow) return { nodes: [], edges: [] };
    return buildGraph(workflow);
  }, [workflow]);

  const stepCount = nodes.length;
  const workflowName = workflow?.name ? formatStepName(workflow.name) : id ? formatStepName(id) : '';
  const description = id ? WORKFLOW_DESCRIPTIONS[id] : undefined;

  const handleRunClick = useCallback(() => {
    setShowInputForm(true);
  }, []);

  const handleFormSubmit = useCallback(async (inputData: Record<string, unknown>) => {
    try {
      await executeMutation.mutateAsync(inputData);
      setFastPoll(true);
      setShowInputForm(false);
      toast.success('Workflow triggered');
    } catch (err) {
      toast.error('Failed to trigger workflow', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [executeMutation]);

  const toggleExpand = useCallback((runId: string) => {
    setExpandedRunId((prev) => (prev === runId ? null : runId));
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-background shrink-0">
        <Link
          to="/ai/workflows"
          className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Workflows
        </Link>
        <div className="w-px h-5 bg-border" />
        <Workflow className="h-4 w-4 text-blue-400" />
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{workflowName}</span>
            <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{id}</span>
          </div>
          {description && (
            <span className="text-xs text-muted-foreground max-w-2xl leading-relaxed">{description}</span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-3">
          {stepCount > 0 && (
            <span className="text-sm text-muted-foreground">{stepCount} steps</span>
          )}
          {!showInputForm && (
            <Button
              size="sm"
              onClick={handleRunClick}
              disabled={isLoading || !!error || executeMutation.isPending}
              className="gap-1.5"
            >
              {executeMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              {executeMutation.isPending ? 'Running...' : 'Run'}
            </Button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="h-[320px] min-h-[260px] bg-[#080b14] shrink-0">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Loading workflow...
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full text-red-400">
            Failed to load workflow. Is Mastra running?
          </div>
        )}
        {workflow && (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            nodesDraggable={false}
            nodesConnectable={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#1e293b" gap={20} />
            <Controls
              showInteractive={false}
              className="!bg-slate-800 !border-slate-700 !shadow-lg [&>button]:!bg-slate-800 [&>button]:!border-slate-700 [&>button]:!text-slate-400 [&>button:hover]:!bg-slate-700"
            />
          </ReactFlow>
        )}
      </div>

      {/* Input Form */}
      {showInputForm && workflow && (
        <div className="border-t border-border bg-background p-4 shrink-0">
          <WorkflowInputForm
            inputSchema={workflow.inputSchema}
            isPending={executeMutation.isPending}
            onSubmit={handleFormSubmit}
            onCancel={() => setShowInputForm(false)}
          />
        </div>
      )}

      {/* Runs Section */}
      <div className="flex-1 border-t border-border bg-background p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Recent Runs</h2>
          <span className="text-xs text-muted-foreground">({totalRuns} total)</span>
          {activeRunExists && (
            <span className="flex items-center gap-1 text-xs text-blue-400 ml-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Live
            </span>
          )}
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Run ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No runs yet. Click <strong>Run</strong> to execute this workflow.
                  </TableCell>
                </TableRow>
              ) : (
                displayRuns.map((run) => {
                  const isExpanded = expandedRunId === run.runId;
                  return (
                    <TableRow
                      key={run.runId}
                      className="group cursor-pointer"
                      onClick={() => toggleExpand(run.runId)}
                    >
                      <TableCell className="w-8 pr-0">
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {run.runId.length > 12 ? `${run.runId.slice(0, 12)}...` : run.runId}
                      </TableCell>
                      <TableCell>
                        <RunStatusBadge status={run.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatTimestamp(run.timestamp)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {run.timestamp ? formatDuration(run.timestamp) : '\u2014'}
                      </TableCell>
                      <TableCell className="text-sm text-red-400 max-w-xs truncate">
                        {run.error?.message ?? '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Expanded detail panel - rendered outside table for proper layout */}
          {expandedRunId && (
            (() => {
              const run = displayRuns.find((r) => r.runId === expandedRunId);
              return run ? <RunDetailPanel run={run} /> : null;
            })()
          )}
        </div>
      </div>

    </div>
  );
}
